/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useContext, useState } from 'react'
import { useStore } from 'zustand'
import { AuthStoreContext } from './AuthProvider'
import { UserInformation } from './AuthStoreState'
import {
  clearSession,
  fetchUserData,
  generateAuthorizationParams,
  generatePKCEValues,
  getAuthorizationData,
  postLoginSession,
  postRefreshSession,
  postRevokeToken,
} from './utils'
import moment from 'moment'

const AUTH_TOKEN_INFO = '/auth/oauth/v2/tokeninfo'
const ENTERPRISE_USER_AUTHORIZATIONS = '/enterprise/authorization/v2/user-authorizations'

export const useAuth = () => {
  // const devSettings = useDevSettings()
  const storeContext = useContext(AuthStoreContext)
  if (!storeContext) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  const {
    user,
    loginData,
    isAuthenticated,
    settings,
    pkceValues,
    tokenExpirationDate,
    setTokenExpiresIn,
    setTokenExpirationDate,
    setPKCEValues,
    setLoginData,
    handleTokenRefresh,
    setIsAuthenticated,
    setAuthorizationData,
    setUserData,
    clearAuthInfo,
  } = useStore(storeContext)

  const [retries, setRetries] = useState(0)
  const [forceUpdateToggle, setForceUpdateToggle] = useState(false)

  
  const login = async () => {
    if (!(isAuthenticated || settings.disabledForDevelopment)) {
      if (!(location.href.search('code') > -1)) {
        console.log(window.location.href)
        
        sessionStorage.setItem('preLoginParams', window.location.href.toString())
        

        const newPKCEValues = generatePKCEValues()
        const params = generateAuthorizationParams(
          newPKCEValues.challenge,
          settings.publicClientID,
          settings.redirectURI,
          settings.scope?.join('%20')
        )
        const pkceURLParam = settings.pkceLoginURL ? `&pkceLoginURL=${settings.pkceLoginURL}` : ''

        setPKCEValues(newPKCEValues)

        location.href = settings.mobileURL
          ? `${settings.mobileURL}${params}${pkceURLParam}`
          : `${settings.baseURL}${params}`
      } else if (pkceValues) {
        const urlSearchParams = new URL(location.href).searchParams
        const code = urlSearchParams.get('code')
        const postUrl = `${settings.tokenURL}`
        const data = `client_id=${settings.publicClientID}&code_verifier=${pkceValues.verifier}&code=${code}&grant_type=authorization_code&redirect_uri=${settings.redirectURI}`

        try {
          const newLoginData = await postLoginSession(
            postUrl,
            data,
            settings.baseURL,
            settings.publicClientID,
            settings.redirectURI,
            settings.revokeURL
          )
          setLoginData(newLoginData)
          setTokenExpiresIn(newLoginData.expires_in)
          setIsAuthenticated(true)

          const paramUrl = sessionStorage.getItem('preLoginParams')

          if(paramUrl){
            location.href = paramUrl
            sessionStorage.removeItem('preLoginParams')
          } else {
            location.href = settings.redirectURI
          }
          

          if (!settings.skipAuthorization) {
            const authorizationUrl = `${settings.baseURL}${ENTERPRISE_USER_AUTHORIZATIONS}`
            const authorizationData = await getAuthorizationData(
              authorizationUrl,
              settings.redirectURI,
              newLoginData.access_token
            )
            setAuthorizationData(authorizationData)
          }
          if (settings.getUserData) {
            const tokenInfoUrl = `${settings.baseURL}${AUTH_TOKEN_INFO}`
            const userData = await fetchUserData(
              tokenInfoUrl,
              settings.redirectURI,
              newLoginData.access_token
            )
            setUserData(userData)
          }
          setForceUpdateToggle(!forceUpdateToggle)
        } catch (err) {
          // eslint-disable-next-line require-atomic-updates
          location.href = settings.redirectURI
        }
      } else {
        location.href = settings.redirectURI
      }
    }
  }
  const logout = async ({ noRedirect }: { noRedirect?: boolean } = {}) => {
    try {
      await clearSession(loginData, noRedirect)
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('Error logging out:', ex)
    } finally {
      clearAuthInfo()
    }
  }

  const refreshToken = async () => {
    if (loginData == null) {
      throw Error('Cannot refresh token because there is no loginData')
    }
    setTokenExpirationDate(null)
    const oldDate = `${tokenExpirationDate}`
    try {
      const newLoginData = await postRefreshSession(
        settings.tokenURL,
        settings.publicClientID,
        loginData.refresh_token
      )

      handleTokenRefresh(newLoginData)
      setRetries(0)
    } catch (ex) {
      if (retries == 3) {
        alert('It seems your session expired unexpectedly. Click OK to log out.')
        logout()
      }
      setRetries(v => v + 1)
      const retryDate = moment(oldDate).add(10, 'seconds')
      setTokenExpirationDate(retryDate.toString())
      // eslint-disable-next-line no-console
      console.error('Error refreshing token:', ex)
    }
  }

  const logoutRevoke = async () => {
    if (loginData == null) {
      throw Error('Cannot revoke token because there is no loginData')
    }
    setTokenExpirationDate(null)
    try {
      await postRevokeToken(settings.publicClientID, settings.revokeURL, loginData.access_token)
      clearAuthInfo()
    } catch (ex) {
      // eslint-disable-next-line no-console
      console.error('Error refreshing token:', ex)
    }
  }

  const mapUser = (user?: UserInformation) => {
    if (!user) {
      return undefined
    }

    // if (devSettings.customRoles !== null) {
    //   user.roles = devSettings.customRoles
    // }

    return user
  }

  return {
    login,
    logout,
    logoutRevoke,
    refreshToken,
    isAuthenticated,
    tokenExpirationDate,
    user: mapUser(user),
  }
}
